"use client";

import dynamic from "next/dynamic";
import { useEffect, useState } from "react";

/**
 * The concierge is ~500 lines of chat UI that sits on every page and is
 * opened by a small fraction of visitors. Mounted eagerly, its JavaScript
 * was parsed and hydrated during the load of every single page — work a
 * mid-range phone on a mobile network pays for before it can respond to a
 * tap, which is what "interaction to next paint" measures.
 *
 * So it is loaded once the browser is idle instead. The button appears a
 * moment later than the rest of the page, which nobody waiting to read an
 * article will notice, and the chat behaves identically once it is there.
 *
 * A 2.5s ceiling, because requestIdleCallback may never fire on a page that
 * stays busy, and the widget should still arrive on a slow device.
 */
const AIConcierge = dynamic(() => import("@/components/AIConcierge").then((m) => m.AIConcierge), {
  ssr: false,
});

export function DeferredAIConcierge() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const show = () => {
      if (!cancelled) setReady(true);
    };
    const idle =
      typeof window.requestIdleCallback === "function"
        ? window.requestIdleCallback(show, { timeout: 2500 })
        : null;
    const timer = window.setTimeout(show, 2500);
    return () => {
      cancelled = true;
      if (idle !== null) window.cancelIdleCallback?.(idle);
      window.clearTimeout(timer);
    };
  }, []);

  return ready ? <AIConcierge /> : null;
}
